/**
 * "How it works" — the method explainer (FR-27, §8, §14).
 *
 * Every number this tool shows is derived, and a derived number nobody can
 * interrogate is just an opinion with extra decimal places. The recommendation
 * cards already break a score into weighted components (FR-27), but the
 * components were only ever labelled — "Scarcity 0.090" says nothing about what
 * scarcity *is*, where 0.090 came from, or that the user can change it.
 *
 * Two decisions shape this panel:
 *
 *  - **It reads live state, not a static copy of the docs.** The weights,
 *    baseline, and active source it describes are the ones currently in effect,
 *    so it explains what is actually happening to this user rather than what
 *    the defaults happen to be. A hardcoded explainer would drift the first
 *    time somebody moved a slider.
 *  - **It says what each number cannot tell you.** Curve-estimated projections
 *    and a thin post-150 ADP tail are real limitations (§4.5, FR-33), and the
 *    honest move is to name them here rather than let a confident-looking
 *    decimal imply precision the input never had.
 */

import { el, clear } from './dom.ts';
import type { AppState } from '../state/store.ts';
import { BASELINE_METHODS } from '../core/presets.ts';
import type { EngineWeights } from '../core/types.ts';
import { BLUEPRINT_BASE_WEIGHTS } from '../core/blueprint.ts';
import { blueprintMetadataTitle, formatBlueprintFreshness } from './blueprintFreshness.ts';

/** Board column glossary — mirrors the header row rendered in `app.ts`. */
const COLUMNS: { label: string; body: string }[] = [
  { label: '#', body: 'Position on the board, under whichever source is currently sorting it.' },
  { label: 'Player', body: 'Struck through once drafted, by anyone. Tap the row for the full breakdown.' },
  { label: 'Pos', body: 'Position, that player’s rank within it, and NFL team. FA means no team.' },
  {
    label: 'Tier',
    body: 'Players grouped by projected value within their position, with the model\'s forecast uncertainty setting how large a meaningful gap is. In Blueprint these tiers come from the complete dated snapshot and stay stable as players are drafted; the T# chip is authoritative and color is a fast scan.',
  },
  { label: 'Bye', body: 'Bye week. Red with an exclamation mark means another player already on your roster at the same position shares that bye week.' },
  {
    label: 'ADP',
    body: 'Average draft position across public drafts of your league’s size and scoring. Green means you would be taking the player well after ADP, amber is roughly market price, and red means a substantial reach at your next pick.',
  },
  {
    label: 'VOR / Gain',
    body: 'Value over replacement normally means projected points above the last startable player at the same position. Blueprint labels its version Gain: marginal lineup value over the contextual replacement baseline. Green is clearly above replacement, amber is near replacement, and red is below it.',
  },
  { label: 'Drafted', body: 'Pick number and team, once gone. "(manual)" means you entered it by hand.' },
];

const COMPONENTS: { key: keyof EngineWeights; label: string; body: string }[] = [
  {
    key: 'vor',
    label: 'Value',
    body: 'Points above a replacement-level player at the same position, normalised across the board. The engine’s main signal.',
  },
  {
    key: 'rank',
    label: 'Ranking',
    body: 'Where the player sits on your active source, as a percentile. This is how your own list gets a vote.',
  },
  {
    key: 'scarcity',
    label: 'Scarcity',
    body: 'How fast the position is draining relative to the rest of the board, plus how thin the current tier is.',
  },
  {
    key: 'need',
    label: 'Roster fit',
    body: 'How much less a player is worth to your lineup than to an empty one. Zero while you still have a slot open at his position — your roster has nothing to say yet, and Value already said it — and negative once his position is full. Depth is discounted, never zeroed: a fourth running back is worth less than a first tight end, but not nothing.',
  },
  {
    key: 'availability',
    label: 'Don’t lose him',
    body: 'What waiting would cost: the chance he is gone by the next pick you could make, multiplied by how much worse your fallback at that position would be. If you are on the clock, the model skips this pick and asks whether he makes it back on your following turn. This is what produces "don’t reach — he’ll be back to you".',
  },
  {
    key: 'lookahead',
    label: 'Next pick',
    body: 'The only term that asks about a pick you have not made yet. It scores the best follow-up this choice would leave you one turn later, evaluated against the roster you would have *after* making it — so a quarterback that fills your only quarterback slot is credited with the back or receiver you would take next, not with a second quarterback. That is what separates the first quarterback from the fifth when the slot is open, and no single-pick term can do it.',
  },
  {
    key: 'bye',
    label: 'Bye',
    body: 'A small penalty when a bye week stacks badly enough that you could not field a starter that week.',
  },
];

export function renderMethodPanel(host: HTMLElement, state: AppState, onClose: () => void): void {
  clear(host);

  const weights = state.config.engine.weights;
  const baselineId = state.config.engine.baseline_method;
  const baseline = BASELINE_METHODS.find((b) => b.id === baselineId);
  const activeId = state.config.active_source_id;
  const activeLabel =
    activeId === 'blend'
      ? 'Blend'
      : activeId === 'value'
        ? 'Value'
      : activeId === 'dynasty'
        ? 'Dynasty'
        : activeId === 'blueprint'
          ? 'Blueprint'
        : state.sources.find((s) => s.source_id === activeId)?.source_name ?? activeId;

  const section = (title: string, ...children: (Node | string | null)[]) =>
    el('section', { class: 'method-section' }, el('h3', {}, title), ...children);

  const dl = (items: { label: string; body: string; trailing?: string }[]) => {
    const list = el('dl', { class: 'method-list' });
    for (const item of items) {
      list.appendChild(
        el(
          'div',
          { class: 'method-item' },
          el(
            'dt',
            {},
            item.label,
            item.trailing ? el('span', { class: 'method-weight' }, item.trailing) : null,
          ),
          el('dd', {}, item.body),
        ),
      );
    }
    return list;
  };

  const body = el(
    'div',
    { class: 'method-body' },

    section(
      'What is sorting the board right now',
      el(
        'p',
        {},
        `Currently: ${activeLabel}. The switch at the top of the screen changes it, and nothing about your draft is lost when you do.`,
      ),
      dl([
        {
          label: 'A named source',
          body: 'Your own imported list, or one of the sources that loaded automatically. The board is that list, in that order.',
        },
        {
          label: 'Blend',
          body: 'Every loaded source mixed together by weight. Blending happens on rank percentile rather than raw rank, so a 100-player list and a 400-player list stay comparable.',
        },
        {
          label: 'Value',
          body: 'Ignores rank order and sorts by value over replacement instead, so the board reads as one cross-position value list rather than four positional ones.',
        },
        {
          label: 'Dynasty',
          body: 'Asks a different question: not who scores most this season, but who is worth most over the next several. Future seasons are discounted, so a player who is better now still wins ties. Opens automatically for a rookie draft.',
        },
      ]),
    ),

    section(
      'Blueprint mode',
      el(
        'p',
        {},
        'Blueprint is a separate research-based draft mode. It starts with a dated player forecast range, then recalculates the decision in your league and on your roster. It can change both the board order and Take next; Blend, Value, Dynasty, and named sources keep their existing behavior.',
      ),
      dl([
        {
          label: 'Marginal lineup value',
          body: 'Expected points above a contextual replacement player, adjusted for the value this player adds to your actual open lineup slots.',
          trailing: `base ${BLUEPRINT_BASE_WEIGHTS.marginal_value.toFixed(2)}`,
        },
        {
          label: 'Ranking residual',
          body: 'The residual between imported ranking sources and the forecast model. It is a tie-breaker, not a hidden expert feed.',
          trailing: `base ${BLUEPRINT_BASE_WEIGHTS.expert_residual.toFixed(2)}`,
        },
        {
          label: 'Scarcity + roster need',
          body: 'Dynamic position pressure and the urgency created by the positions still open on your roster. Blueprint tiers themselves are forecast-based and stable; this component is a separate draft-state signal.',
          trailing: `base ${(BLUEPRINT_BASE_WEIGHTS.scarcity + BLUEPRINT_BASE_WEIGHTS.roster_need).toFixed(2)}`,
        },
        {
          label: 'Wait cost',
          body: 'The chance the player disappears before the next pick you could make multiplied by the expected loss against the fallback. If you are on the clock, that means your following turn; if ADP is missing, timing is shown as unknown.',
          trailing: `base ${BLUEPRINT_BASE_WEIGHTS.wait_cost.toFixed(2)}`,
        },
        {
          label: 'Upside + bye cost',
          body: 'Upper-tail outcomes and breakout probability add a small late-draft upside signal; bye conflicts stay a small penalty.',
          trailing: `base ${(BLUEPRINT_BASE_WEIGHTS.upside + BLUEPRINT_BASE_WEIGHTS.bye_cost).toFixed(2)}`,
        },
      ]),
      el(
        'p',
        { class: 'method-caveat', title: state.blueprintSnapshot ? blueprintMetadataTitle(state.blueprintSnapshot) : undefined },
        state.blueprintSnapshot
          ? `Snapshot ${state.blueprintSnapshot.model_version}, ${formatBlueprintFreshness(state.blueprintSnapshot)}. Expires ${new Date(state.blueprintSnapshot.expires_at).toLocaleString()}. Source: ${state.blueprintSnapshot.source}.`
          : 'Blueprint is currently disabled because no valid forecast snapshot is available. The app does not silently fall back to another mode.',
      ),
      el(
        'p',
        { class: 'method-caveat' },
        'The first release uses open-data model artifacts and browser-side roster scoring. Imported rankings can inform the residual, but there is no licensed expert ensemble or server-side roster state in this mode yet.',
      ),
    ),

    section(
      'How the dynasty numbers work',
      el(
        'p',
        {},
        'Two populations, priced two ways, because they carry different information.',
      ),
      dl([
        {
          label: 'Incoming rookies',
          body:
            'Priced from where they went in the actual NFL draft, which is the aggregated judgement of every professional scouting department and the best signal available about someone with no professional snaps. The effect is large: a running back taken in the top twelve posts a startable season about 93% of the time, one taken after pick 160 about 6% of the time.',
        },
        {
          label: 'Bust risk is already in the number',
          body:
            'The expectation at each draft slot averages in every player ever taken there, including the ones who never played a down. So it is what a pick is worth on average, not what it looks like when it works. A late-round rookie is not shown as a starter who might miss.',
        },
        {
          label: 'Everyone else',
          body:
            "This season's projection carried forward and decayed by an aging curve fitted for their position. Running backs fall off hardest, receivers hold their value, quarterbacks decline slowest.",
        },
        {
          label: 'Why the aging curve is not the obvious one',
          body:
            'Averaging points by age says 34-year-old receivers outproduce 24-year-olds — not because age helps, but because only the great ones are still playing at 34. Comparing each player against himself a year later removes that illusion, and is what these curves do.',
        },
        {
          label: 'Where it is weakest',
          body:
            'It knows nothing about landing spot, depth chart, injury history or college production, and a rookie with no entry in the draft table is priced on age alone. Numbers shown dimmed are ones where age was unknown.',
        },
      ]),
    ),

    section('What each board column means', dl(COLUMNS)),

    section(
      'How a player gets a score',
      el(
        'p',
        {},
        'Each recommendation is a weighted sum of six terms. Every term is put on a common scale first, so a weight is a share of the decision and the six can be read against each other — a slider at twice another moves the board about twice as much. The bars on a recommendation card are those terms after weighting, which is why a bar can be negative and why the bars add up to the score beside the player’s name.',
      ),
      dl(
        COMPONENTS.map((c) => ({
          label: c.label,
          body: c.body,
          trailing: `weight ${weights[c.key].toFixed(2)}`,
        })),
      ),
      el(
        'p',
        { class: 'hint' },
        'These are your current weights, not fixed opinion — Recommendation settings in the More menu changes them live, against your real board, and can put them back.',
      ),
    ),

    section(
      'Where the points come from',
      el(
        'p',
        {},
        `Value over replacement compares a player to the last one at his position you could realistically start, so a running back and a tight end become comparable. Where that replacement line sits is the baseline, currently ${baseline?.label ?? baselineId}: ${baseline?.description ?? ''}`,
      ),
      el(
        'p',
        { class: 'method-caveat' },
        'The projections behind it are estimated from a player’s positional rank using curves fitted to past seasons — they are not per-player statistical forecasts, and the tool labels them "est." wherever they appear. They cannot disagree with your ranking source about a player, because they are derived from it. Treat the gaps between players as the signal, not the absolute totals.',
      ),
    ),

    section(
      'Timing: will he last?',
      el(
        'p',
        {},
        'The "wait, or take him now?" read turns ADP and its spread into a probability that a player survives to the next pick you could make, then compares him against who you would realistically get instead if you waited. When you are on the clock, it evaluates your following turn rather than the pick already in front of you.',
      ),
      el(
        'p',
        { class: 'method-caveat' },
        'It is only as good as the ADP behind it. Public ADP thins out badly after roughly pick 150 and in unusual league formats, and where the sample is too thin the tool says the timing is unknown rather than guessing.',
      ),
    ),

    section(
      'Disagreement between your sources',
      el(
        'p',
        {},
        'When more than one ranking source is loaded, each player carries a disagreement score: the spread between where your sources place him, on a 0–100 scale. A high score is the most interesting thing a multi-source tool knows — it means the consensus is thin and the pick is genuinely a judgement call.',
      ),
    ),

    section(
      'The two kinds of write-up',
      dl([
        {
          label: 'Scouting note',
          body: 'General, real-world context — role, opportunity, scheme, situation — researched and written ahead of time, and stamped with the date it was researched so you can judge how stale it is. It does not know anything about your roster.',
        },
        {
          label: 'The case',
          body: 'Generated live from your league, your roster, and the current pick. It never invents a statistic: every number in it is one this tool computed or read from a source you loaded.',
        },
      ]),
    ),
  );

  host.appendChild(
    el(
      'div',
      { class: 'modal method-modal', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'How it works' },
      el(
        'header',
        { class: 'modal-head' },
        el('h2', {}, 'How it works'),
        el('button', { class: 'btn btn-quiet', type: 'button', onClick: onClose, 'aria-label': 'Close' }, '×'),
      ),
      body,
      el(
        'footer',
        { class: 'modal-foot' },
        el('button', { class: 'btn btn-primary', type: 'button', onClick: onClose }, 'Done'),
      ),
    ),
  );
}
